/**
 * socialFlow.ts — the six steps of a social post's life (Articles → Drafts → Pictures → Review → Scheduled → Posted) and
 * how each step's status is worked out from plain counts. Pure, so the server fills the counts and the screen only draws
 * the answer. Same five statuses as the ads flow; nothing is shown as done unless the records for it exist.
 */
import { z } from "zod";
import { FLOW_STATUSES, type FlowStatus } from "./adsFlow.ts";

export const SOCIAL_FLOW_STEPS = ["articles", "drafts", "pictures", "review", "scheduled", "posted"] as const;
export type SocialFlowStepId = (typeof SOCIAL_FLOW_STEPS)[number];
export const SOCIAL_FLOW_LABEL: Record<SocialFlowStepId, string> = { articles: "Articles", drafts: "Drafts", pictures: "Pictures", review: "Review", scheduled: "Scheduled", posted: "Posted" };

export const socialFlowCountsSchema = z.object({
  /** Published articles of the last 45 days with no post yet. */
  articles: z.number().int().min(0),
  /** Posts written but not yet sent for approval. */
  drafts: z.number().int().min(0),
  /** Open posts (draft, waiting or approved and not yet posted) with no picture or video. */
  noPicture: z.number().int().min(0),
  /** Open posts in total, for "nothing yet" versus "all done". */
  open: z.number().int().min(0),
  inReview: z.number().int().min(0),
  /** Approved and not yet posted. */
  approved: z.number().int().min(0),
  /** Of those, handed to Sociamonials. */
  sent: z.number().int().min(0),
  /** Delivered by Sociamonials in the last 30 days. */
  posted: z.number().int().min(0),
  /** Sociamonials could not send them. */
  failed: z.number().int().min(0),
});
export type SocialFlowCounts = z.infer<typeof socialFlowCountsSchema>;

export const socialFlowStepSchema = z.object({
  id: z.enum(SOCIAL_FLOW_STEPS),
  label: z.string(),
  status: z.enum(FLOW_STATUSES),
  count: z.number().int().nullable(),
  unit: z.string(),
});
export type SocialFlowStep = z.infer<typeof socialFlowStepSchema>;

export const socialFlowSchema = z.object({
  counts: socialFlowCountsSchema,
  steps: z.array(socialFlowStepSchema).length(SOCIAL_FLOW_STEPS.length),
  headline: z.string(),
  action: z.enum(["review", "none"]),
  problem: z.string().nullable(),
});
export type SocialFlow = z.infer<typeof socialFlowSchema>;

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

export function buildSocialSteps(c: SocialFlowCounts): SocialFlowStep[] {
  const step = (id: SocialFlowStepId, status: FlowStatus, count: number | null, unit: string): SocialFlowStep => ({ id, label: SOCIAL_FLOW_LABEL[id], status, count, unit });
  const anything = c.open > 0 || c.posted > 0;
  return [
    step("articles", c.articles > 0 ? "waiting" : anything ? "done" : "waiting", c.articles > 0 || anything ? c.articles : null, plural(c.articles, "article needs a post", "articles need a post")),
    step("drafts", c.drafts > 0 ? "you" : anything ? "done" : "waiting", c.drafts > 0 || anything ? c.drafts : null, plural(c.drafts, "draft to send", "drafts to send")),
    step("pictures", c.noPicture > 0 ? "waiting" : c.open > 0 ? "done" : "waiting", c.noPicture > 0 ? c.noPicture : c.open > 0 ? 0 : null, plural(c.noPicture, "post has no picture", "posts have no picture")),
    step("review", c.inReview > 0 ? "you" : anything ? "done" : "waiting", c.inReview > 0 || anything ? c.inReview : null, c.inReview > 0 ? "to review" : "left to review"),
    step("scheduled", c.failed > 0 ? "failed" : c.approved > 0 ? "done" : "waiting", c.failed > 0 ? c.failed : c.approved > 0 ? c.approved : null, c.failed > 0 ? plural(c.failed, "could not be sent", "could not be sent") : c.sent > 0 ? `approved, ${c.sent} sent` : "approved"),
    step("posted", c.posted > 0 ? "done" : "waiting", c.posted > 0 ? c.posted : null, plural(c.posted, "post this month", "posts this month")),
  ];
}

export function buildSocialFlow(c: SocialFlowCounts): SocialFlow {
  const steps = buildSocialSteps(c);
  const problem = c.failed > 0 ? `${c.failed} ${plural(c.failed, "post", "posts")} could not be sent to Sociamonials. Open ${plural(c.failed, "it", "them")} in All posts and try again.` : null;
  let headline: string;
  let action: SocialFlow["action"] = "none";
  if (c.inReview > 0) {
    headline = `${c.inReview} ${plural(c.inReview, "post is", "posts are")} waiting for you`;
    action = "review";
  } else if (c.failed > 0) headline = "A post could not be sent";
  else if (c.drafts > 0) headline = `${c.drafts} ${plural(c.drafts, "draft is", "drafts are")} not sent for approval yet`;
  else if (c.articles > 0) headline = `${c.articles} ${plural(c.articles, "article has", "articles have")} no post yet`;
  else if (c.open > 0 || c.posted > 0) headline = "Nothing is waiting for you";
  else headline = "No posts yet";
  return { counts: c, steps, headline, action, problem };
}
